export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
}

export interface OrganizationDetails extends OrganizationSummary {
  createdAt?: string;
  invitations?: unknown[];
  members?: unknown[];
  metadata?: Record<string, unknown> | string | null;
}

export interface ActiveMemberRole {
  role: 'owner' | 'admin' | 'member';
}

export interface SessionLike {
  session?: {
    activeOrganizationId?: string | null;
  } | null;
  user?: {
    id?: string | null;
  } | null;
}

export const organizationQueryKeys = {
  activeMemberRole(orgId: string | null, userId: string | null) {
    return ['auth', 'active-member-role', orgId, userId] as const;
  },
  activeOrganization(orgId: string | null, userId: string | null) {
    return ['auth', 'active-organization', orgId, userId] as const;
  },
  organizations(userId: string | null) {
    return ['auth', 'organizations', userId] as const;
  },
};

async function authApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/auth${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const message = await response.text().catch(() => 'Auth request failed');
    throw new Error(message || 'Auth request failed');
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
}

export function listOrganizations() {
  return authApiFetch<OrganizationSummary[]>('/organization/list', {
    method: 'GET',
  });
}

export function getActiveMemberRole() {
  return authApiFetch<ActiveMemberRole>(
    '/organization/get-active-member-role',
    {
      method: 'GET',
    },
  );
}

export function getActiveOrganization() {
  return authApiFetch<OrganizationDetails>(
    '/organization/get-full-organization',
    {
      method: 'GET',
    },
  );
}

export function setActiveOrganization(organizationId: string) {
  return authApiFetch<OrganizationSummary>('/organization/set-active', {
    body: JSON.stringify({ organizationId }),
    method: 'POST',
  });
}

export function updateOrganizationSettings(input: {
  name?: string;
  slug?: string;
  logo?: string | null;
  organizationId?: string;
}) {
  return authApiFetch<OrganizationDetails>('/organization/update', {
    body: JSON.stringify({
      data: {
        logo: input.logo || undefined,
        name: input.name,
        slug: input.slug,
      },
      organizationId: input.organizationId,
    }),
    method: 'POST',
  });
}

export function listOrgMembers() {
  return authApiFetch<{ members: Array<Record<string, unknown>> }>(
    '/organization/members',
    { method: 'GET' },
  );
}

export async function activateFirstOrganization() {
  const organizations = await listOrganizations();
  const [firstOrganization] = organizations;

  if (!firstOrganization) {
    return null;
  }

  await setActiveOrganization(firstOrganization.id);
  return firstOrganization.id;
}

export interface UseOrganizationStateOptions {
  autoActivate?: boolean;
  refetchSession: () => Promise<unknown>;
  session: SessionLike | null;
}
